import { useState, useMemo, useEffect } from 'react';
import { AlertType, CloudType, Alert, Severity } from '../../types/alerts';
import { Send, Sparkles, Brain, BookOpen, GitBranch, Loader2, Check, X, RotateCcw, Server, Database, Key, EyeOff, ShieldAlert, Globe, UserX, Unlock, GitMerge, Activity, Cpu, Cloud, FileCode, AlertTriangle, ArrowUpRight } from 'lucide-react';
import { Button } from '../../../../ui/common-ui/button';
import { Checkbox } from '../../../../ui/common-ui/checkbox';
import { Badge } from '../../../../ui/common-ui/badge';
import { toast } from 'sonner';
import { AlertCard } from './AlertCard';
import { AlertDetailsEdit } from './AlertDetailsEdit';
import { CreateAlertSelectors } from './CreateAlertSelectors';
import { generateAlert, GeneratedAlertResponse, saveAlerts, CloudAlertDTO, fetchLlmTaskStatus } from '../../services/api';
import {
  LLM_PROVIDER_KEY_NOT_CONFIGURED_MESSAGE,
  LLM_TASK_ALREADY_IN_PROGRESS_MESSAGE,
  useLLMProviderAvailability,
} from '../../../hooks/useLLMProviderAvailability';

interface CreateAlertChatProps {
  selectedCloud: CloudType;
  tenantName: string;
  cloudId: string;
  onAlertsAdded: (alerts: Alert[]) => void;
  onRefreshAlerts: () => void;
}

export type SuggestionItem = {
  title: string;
  description: string;
  prompt: string;
  icon: typeof Brain;
};

type SuggestionMap = Record<CloudType, Record<AlertType, SuggestionItem[]>>;

const suggestionsByCloudAndType: SuggestionMap = {
  AWS: {
    behaviour: [
      { title: 'EC2 Launch Spikes', description: 'Detect sudden bursts of EC2 instance creation', prompt: 'Unusual EC2 instance launch patterns', icon: Server },
      { title: 'S3 Exfiltration', description: 'Flag anomalous large data transfers from sensitive buckets', prompt: 'Anomalous S3 access behavior', icon: Database },
      { title: 'IAM Credential Theft', description: 'Identify compromised keys used from unusual locations', prompt: 'Suspicious IAM credential usage', icon: Key },
      { title: 'CloudTrail Evasion', description: 'Detect irregular or missing CloudTrail activity', prompt: 'Irregular CloudTrail activity patterns', icon: EyeOff },
    ],
    rule: [
      { title: 'Open Security Groups', description: 'Find rules allowing all inbound traffic', prompt: 'Security group allows all inbound traffic', icon: ShieldAlert },
      { title: 'Public S3 Buckets', description: 'Identify buckets made publicly accessible', prompt: 'S3 bucket made public', icon: Globe },
      { title: 'Root Account Usage', description: 'Alert on any operations performed by root', prompt: 'Root account used for operations', icon: AlertTriangle },
      { title: 'MFA Disabled', description: 'Detect IAM users without Multi-Factor Authentication', prompt: 'MFA disabled for IAM user', icon: Unlock },
    ],
    sequential: [
      { title: 'Privilege Escalation', description: 'Key creation followed by unauthorized escalation', prompt: 'IAM key creation followed by privilege escalation', icon: GitMerge },
      { title: 'Data Exfiltration', description: 'Instance compromise leading to data theft', prompt: 'EC2 instance compromise to data exfiltration', icon: FileCode },
      { title: 'Lateral Movement', description: 'Credential theft leading to network traversal', prompt: 'Credential theft to lateral movement', icon: Activity },
      { title: 'Resource Hijacking', description: 'Reconnaissance followed by resource abuse', prompt: 'Reconnaissance to resource hijacking', icon: Cpu },
    ],
  },
  GCP: {
    behaviour: [
      { title: 'VM Creation Spikes', description: 'Detect unusual Compute Engine VM creation', prompt: 'Unusual Compute Engine VM creation', icon: Server },
      { title: 'BigQuery Anomalies', description: 'Flag anomalous BigQuery data access', prompt: 'Anomalous BigQuery data access', icon: Database },
      { title: 'Service Account Abuse', description: 'Identify suspicious service account activity', prompt: 'Suspicious service account activity', icon: UserX },
      { title: 'Cloud Storage Evasion', description: 'Detect irregular Cloud Storage access patterns', prompt: 'Irregular Cloud Storage patterns', icon: Cloud },
    ],
    rule: [
      { title: 'Open Firewall Rules', description: 'Find firewall rules allowing all ingress', prompt: 'Firewall rule allows all ingress', icon: ShieldAlert },
      { title: 'Public Storage Buckets', description: 'Identify Cloud Storage buckets with public access', prompt: 'Cloud Storage bucket public access', icon: Globe },
      { title: 'Key Creation', description: 'Alert on service account key creation', prompt: 'Service account key created', icon: Key },
      { title: 'Audit Logging Disabled', description: 'Detect disabled audit logging configurations', prompt: 'Audit logging disabled', icon: EyeOff },
    ],
    sequential: [
      { title: 'Data Access', description: 'Service account compromise leading to data access', prompt: 'Service account compromise to data access', icon: GitMerge },
      { title: 'Project Takeover', description: 'VM breach leading to project takeover', prompt: 'VM breach to project takeover', icon: Cpu },
      { title: 'Resource Abuse', description: 'API key leak leading to resource abuse', prompt: 'API key leak to resource abuse', icon: Activity },
      { title: 'Identity Escalation', description: 'Identity escalation chain detection', prompt: 'Identity escalation chain detection', icon: Unlock },
    ],
  },
  AZURE: {
    behaviour: [
      { title: 'VM Deployments', description: 'Detect unusual Azure VM deployments', prompt: 'Unusual Azure VM deployments', icon: Server },
      { title: 'Blob Storage Anomalies', description: 'Flag anomalous Blob Storage access', prompt: 'Anomalous Blob Storage access', icon: Database },
      { title: 'Azure AD Sign-ins', description: 'Identify suspicious Azure AD sign-ins', prompt: 'Suspicious Azure AD sign-ins', icon: UserX },
      { title: 'Key Vault Operations', description: 'Detect irregular Key Vault operations', prompt: 'Irregular Key Vault operations', icon: Key },
    ],
    rule: [
      { title: 'Open NSG Rules', description: 'Find NSGs allowing unrestricted access', prompt: 'NSG allows unrestricted access', icon: ShieldAlert },
      { title: 'Public Storage Containers', description: 'Identify storage containers with public access', prompt: 'Storage container public access', icon: Globe },
      { title: 'Guest User Added', description: 'Alert on Azure AD guest user additions', prompt: 'Azure AD guest user added', icon: UserX },
      { title: 'Diagnostic Logging Disabled', description: 'Detect disabled diagnostic logging', prompt: 'Diagnostic logging disabled', icon: EyeOff },
    ],
    sequential: [
      { title: 'Subscription Access', description: 'Credential compromise leading to subscription access', prompt: 'Credential compromise to subscription access', icon: GitMerge },
      { title: 'Managed Identity Abuse', description: 'VM compromise leading to managed identity abuse', prompt: 'VM compromise to managed identity abuse', icon: Activity },
      { title: 'Admin Escalation', description: 'Guest account escalating to admin privileges', prompt: 'Guest account to admin escalation', icon: Unlock },
      { title: 'Secret Exfiltration', description: 'Key Vault breach leading to secret exfiltration', prompt: 'Key Vault breach to secret exfiltration', icon: FileCode },
    ],
  },
};

const alertTypes: { type: AlertType | 'automatic'; label: string; icon: typeof Brain; description: string; color: string }[] = [
  { type: 'automatic', label: 'Automatic', icon: Sparkles, description: 'AI decides based on description', color: 'amber' },
  { type: 'behaviour', label: 'Behaviour', icon: Brain, description: 'ML-based anomaly detection', color: 'purple' },
  { type: 'rule', label: 'Rule', icon: BookOpen, description: 'Pattern-based detection', color: 'blue' },
  { type: 'sequential', label: 'Sequential', icon: GitBranch, description: 'Multi-step attack chains', color: 'primary' },
];

// Helper to determine alert type based on steps count
const getAlertTypeFromSteps = (steps?: Record<string, string>): AlertType => {
  const stepsCount = steps ? Object.keys(steps).length : 0;
  if (stepsCount === 0) return 'behaviour';
  if (stepsCount === 1) return 'rule';
  return 'sequential';
};

// Helper to convert API time window format to UI format
// API format: "2026-01-06T00:00:00-2026-01-06T00:59:59"
// UI format: "12:00 AM - 12:59 AM"
const convertTimeWindowToUIFormat = (timeWindow: string): string => {
  try {
    const parts = timeWindow.split('-');
    if (parts.length < 2) return timeWindow;
    
    // Handle ISO format with date parts
    // The format is: YYYY-MM-DDTHH:MM:SS-YYYY-MM-DDTHH:MM:SS
    // We need to find the T separators
    const firstTIndex = timeWindow.indexOf('T');
    const secondTIndex = timeWindow.lastIndexOf('T');
    
    if (firstTIndex === -1 || secondTIndex === -1 || firstTIndex === secondTIndex) {
      return timeWindow;
    }
    
    // Extract start and end time portions
    const startDateTime = timeWindow.substring(0, secondTIndex - 11); // Before the second date
    const endDateTime = timeWindow.substring(secondTIndex - 10); // From the second date
    
    const startTime = startDateTime.split('T')[1]; // HH:MM:SS
    const endTime = endDateTime.split('T')[1]; // HH:MM:SS
    
    if (!startTime || !endTime) return timeWindow;
    
    // Parse hours and minutes
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    // Convert to 12-hour format
    const formatTime = (hour: number, min: number): string => {
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${min.toString().padStart(2, '0')} ${period}`;
    };
    
    return `${formatTime(startHour, startMin)} - ${formatTime(endHour, endMin)}`;
  } catch (e) {
    console.error('Error parsing time window:', e);
    return timeWindow;
  }
};

// Transform API response to Alert type
const transformApiResponse = (apiAlerts: GeneratedAlertResponse[], cloud: CloudType): Alert[] => {
  return apiAlerts.map((apiAlert, index) => {
    // Convert steps from string keys to number keys
    const steps: Record<number, string> | undefined = apiAlert.steps && Object.keys(apiAlert.steps).length > 0
      ? Object.entries(apiAlert.steps).reduce((acc, [key, value]) => {
          acc[parseInt(key, 10)] = value;
          return acc;
        }, {} as Record<number, string>)
      : undefined;

    // Convert MITRE from Record<code, explanation> to MitreInfo[]
    const mitre = Object.entries(apiAlert.mitre || {}).map(([code, explanation]) => ({
      code,
      key: code,
      explanation,
    }));

    // Normalize severity
    const normalizeSeverity = (sev: string): Severity => {
      const s = sev.toLowerCase();
      if (s === 'critical' || s === 'p0') return 'Critical';
      if (s === 'high' || s === 'p1') return 'High';
      if (s === 'medium' || s === 'p2') return 'Medium';
      return 'Low';
    };

    // Determine alert type from response or steps
    const alertType = getAlertTypeFromSteps(apiAlert.steps);

    return {
      id: Date.now() + index,
      title: apiAlert.title,
      severity: normalizeSeverity(apiAlert.severity),
      type: alertType,
      info: {
        why: apiAlert.info?.why || '',
        what: apiAlert.info?.what || '',
        how: apiAlert.info?.how || '',
      },
      steps,
      mitre,
      isEnabled: true,
      isCustom: true,
      cloud,
      users: apiAlert.users || [],
      resource: apiAlert.resource || [],
      timeWindow: (apiAlert.timeWindow || []).map(convertTimeWindowToUIFormat),
    };
  });
};

// Generate alerts via API
const generateAlertsFromApi = async (
  tenantName: string,
  cloudId: string,
  cloudType: CloudType,
  userPrompt: string,
  alertType: AlertType | 'automatic'
): Promise<Alert[]> => {
  const response = await generateAlert({
    tenantName,
    cloudId,
    cloudType: cloudType === 'AZURE' ? 'AZURE' : cloudType,
    userPrompt,
    alertType: alertType === 'automatic' ? 'AUTOMATIC' : alertType.toUpperCase() as 'RULE' | 'BEHAVIOUR' | 'SEQUENTIAL',
  });

  return transformApiResponse(response, cloudType);
};

// Transform Alert to CloudAlertDTO for save API
const transformAlertToDTO = (alert: Alert): CloudAlertDTO => {
  // Convert steps from number keys to number keys (already correct type)
  const steps: Record<number, string> = alert.steps || {};
  
  // Convert mitre array to Record<string, string>
  const mitre: Record<string, string> = (alert.mitre || []).reduce((acc, m) => {
    acc[m.code] = m.explanation || '';
    return acc;
  }, {} as Record<string, string>);

  // Map alert type
  const alertTypeMap: Record<AlertType, 'BEHAVIOUR' | 'RULE' | 'SEQUENTIAL'> = {
    behaviour: 'BEHAVIOUR',
    rule: 'RULE',
    sequential: 'SEQUENTIAL',
  };

  return {
    title: alert.title,
    severity: alert.severity.toUpperCase(),
    alertType: alertTypeMap[alert.type],
    info: {
      why: alert.info?.why || '',
      what: alert.info?.what || '',
      how: alert.info?.how || '',
    },
    steps,
    mitre,
    isFlagged: !alert.isEnabled,
    isDefault: false,
    users: alert.users || [],
    resource: alert.resource || [],
    timeWindow: alert.timeWindow || [],
  };
};

const LOADING_TEXTS = [
  'Analyzing alert description...',
  'Processing security patterns...',
  'Scanning threat intelligence...',
  'Evaluating detection rules...',
  'Mapping MITRE ATT&CK framework...',
  'Correlating cloud indicators...',
  'Building alert parameters...',
  'Optimizing detection logic...',
  'Finalizing alerts...',
];

export function CreateAlertChat({ selectedCloud, tenantName, cloudId, onAlertsAdded, onRefreshAlerts }: CreateAlertChatProps) {
  const [prompt, setPrompt] = useState('');
  const [selectedType, setSelectedType] = useState<AlertType | 'automatic'>('automatic');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [displayedSuggestions, setDisplayedSuggestions] = useState<SuggestionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [generatedAlerts, setGeneratedAlerts] = useState<Alert[]>([]);
  const [selectedAlertIds, setSelectedAlertIds] = useState<Set<number>>(new Set());
  const [previewAlert, setPreviewAlert] = useState<Alert | null>(null);
  const { hasConfiguredLLMProviderKey } = useLLMProviderAvailability();
  
  // Pre-configuration selections
  const [preSelectedTimeWindows, setPreSelectedTimeWindows] = useState<string[]>([]);
  const [preSelectedUsers, setPreSelectedUsers] = useState<string[]>([]);
  const [preSelectedServiceAccounts, setPreSelectedServiceAccounts] = useState<string[]>([]);
  const [preSelectedResources, setPreSelectedResources] = useState<string[]>([]);
  
  // Loading progress state
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [isCompletingAnimation, setIsCompletingAnimation] = useState(false);
  const [pendingAlerts, setPendingAlerts] = useState<Alert[] | null>(null);

  // LLM task status polling (every 2s) to gate the send button
  const [isCreateAlertTaskInProgress, setIsCreateAlertTaskInProgress] = useState(false);

  useEffect(() => {
    if (!tenantName || !cloudId) {
      setIsCreateAlertTaskInProgress(false);
      return;
    }
    let active = true;
    const poll = async () => {
      try {
        const statuses = await fetchLlmTaskStatus(tenantName, cloudId);
        if (!active) return;
        const task = statuses.find((s) => s.llmTask === 'CREATE_ALERT');
        setIsCreateAlertTaskInProgress(task?.status === 'IN_PROGRESS');
      } catch {
        // ignore polling errors
      }
    };
    poll();
    const intervalId = setInterval(poll, 2000);
    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [tenantName, cloudId]);

  const currentSuggestions = useMemo(() => {
    if (selectedType === 'automatic') {
      // Show a mix of suggestions from all types
      const allSuggestions = [
        ...suggestionsByCloudAndType[selectedCloud]?.behaviour || [],
        ...suggestionsByCloudAndType[selectedCloud]?.rule || [],
        ...suggestionsByCloudAndType[selectedCloud]?.sequential || [],
      ];
      return allSuggestions.slice(0, 4);
    }
    return suggestionsByCloudAndType[selectedCloud]?.[selectedType] || [];
  }, [selectedCloud, selectedType]);

  // Update suggestions immediately when cloud or type changes - no fade transition
  useEffect(() => {
    setDisplayedSuggestions(currentSuggestions);
  }, [currentSuggestions]);

  // Loading progress animation - increments 10% every 1.5s up to 90%
  useEffect(() => {
    if (!isLoading || isCompletingAnimation) return;
    
    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) return prev;
        return prev + 10;
      });
      setLoadingTextIndex(prev => {
        if (prev >= 8) return prev;
        return prev + 1;
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [isLoading, isCompletingAnimation]);

  // Handle completion animation when alerts are ready
  useEffect(() => {
    if (pendingAlerts && isCompletingAnimation) {
      // Complete to 100%
      setLoadingProgress(100);
      setLoadingTextIndex(9); // "Generated!" index
      
      // After showing completion, display the cards
      const timer = setTimeout(() => {
        setGeneratedAlerts(prev => [...pendingAlerts, ...prev]);
        setIsLoading(false);
        setIsCompletingAnimation(false);
        setPendingAlerts(null);
        setLoadingProgress(0);
        setLoadingTextIndex(0);
        toast.success(`Generated ${pendingAlerts.length} alert${pendingAlerts.length !== 1 ? 's' : ''}`);
      }, 800);
      
      return () => clearTimeout(timer);
    }
  }, [pendingAlerts, isCompletingAnimation]);

  const sendPromptBlockedReason = isCreateAlertTaskInProgress
    ? LLM_TASK_ALREADY_IN_PROGRESS_MESSAGE
    : !hasConfiguredLLMProviderKey
      ? LLM_PROVIDER_KEY_NOT_CONFIGURED_MESSAGE
      : '';
  const isSendPromptDisabled = !prompt.trim() || isLoading || isCreateAlertTaskInProgress || !hasConfiguredLLMProviderKey;
  const isPromptInputDisabled = isLoading || isCreateAlertTaskInProgress || !hasConfiguredLLMProviderKey;

  const handleSubmit = async () => {
    if (isSendPromptDisabled) return;
    
    if (!tenantName || !cloudId) {
      toast.error('Please select a tenant and resource first');
      return;
    }
    
    setShowSuggestions(false);
    setIsLoading(true);
    setLoadingProgress(0);
    setLoadingTextIndex(0);
    
    try {
      const newAlerts = await generateAlertsFromApi(
        tenantName,
        cloudId,
        selectedCloud,
        prompt,
        selectedType
      );
      
      // Merge pre-selections with generated alerts (no duplicates)
      const mergedAlerts = newAlerts.map(alert => ({
        ...alert,
        users: [...new Set([...preSelectedUsers, ...(alert.users || [])])],
        resource: [...new Set([...preSelectedResources, ...(alert.resource || [])])],
        timeWindow: [...new Set([...preSelectedTimeWindows, ...(alert.timeWindow || [])])],
        // Service accounts go into users list for backend
        ...(preSelectedServiceAccounts.length > 0 && {
          users: [...new Set([...preSelectedUsers, ...preSelectedServiceAccounts, ...(alert.users || [])])]
        })
      }));
      
      // Store alerts and trigger completion animation
      setPendingAlerts(mergedAlerts);
      setIsCompletingAnimation(true);
      setPrompt('');
    } catch (error) {
      console.error('Failed to generate alerts:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate alerts. Please try again.');
      setIsLoading(false);
      setLoadingProgress(0);
      setLoadingTextIndex(0);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setPrompt(suggestion);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const toggleAlertSelection = (alertId: number) => {
    setSelectedAlertIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(alertId)) {
        newSet.delete(alertId);
      } else {
        newSet.add(alertId);
      }
      return newSet;
    });
  };

  const selectAllAlerts = () => {
    setSelectedAlertIds(new Set(generatedAlerts.map(a => a.id)));
  };

  const deselectAllAlerts = () => {
    setSelectedAlertIds(new Set());
  };


  const addSelectedAlerts = async () => {
    const alertsToAdd = generatedAlerts.filter(a => selectedAlertIds.has(a.id));
    if (alertsToAdd.length === 0) {
      toast.error('Please select at least one alert to add');
      return;
    }

    if (!tenantName || !cloudId) {
      toast.error('Please select a tenant and resource first');
      return;
    }

    setIsSaving(true);
    
    try {
      // Transform alerts to DTOs for API
      const alertDTOs = alertsToAdd.map(transformAlertToDTO);
      
      // Call save API
      await saveAlerts({
        alerts: alertDTOs,
        cloudType: selectedCloud,
        tenantName,
        cloudAccountId: cloudId,
      });
      
      onAlertsAdded(alertsToAdd);
      // Remove added alerts from generated list
      setGeneratedAlerts(prev => prev.filter(a => !selectedAlertIds.has(a.id)));
      setSelectedAlertIds(new Set());
      toast.success(`Added ${alertsToAdd.length} alert${alertsToAdd.length !== 1 ? 's' : ''} to your library`);
      
      // Refresh alerts list from API
      onRefreshAlerts();
    } catch (error) {
      console.error('Failed to save alerts:', error);
      toast.error('Failed to save alerts. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const clearAll = () => {
    setGeneratedAlerts([]);
    setSelectedAlertIds(new Set());
    setShowSuggestions(true);
    setPrompt('');
  };

  const getColorClasses = (color: string, isSelected: boolean) => {
    const unselectedBg = 'bg-gradient-to-r from-card/80 to-card/60 backdrop-blur-xl';
    
    const colors: Record<string, { bg: string; border: string; icon: string; text: string }> = {
      amber: {
        bg: isSelected ? 'bg-amber-500/10' : unselectedBg,
        border: isSelected ? 'border-amber-500/50' : 'border-border',
        icon: isSelected ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground',
        text: isSelected ? 'text-amber-600' : 'text-foreground',
      },
      purple: {
        bg: isSelected ? 'bg-purple-500/10' : unselectedBg,
        border: isSelected ? 'border-purple-500/50' : 'border-border',
        icon: isSelected ? 'bg-purple-500 text-white' : 'bg-muted text-muted-foreground',
        text: isSelected ? 'text-purple-600' : 'text-foreground',
      },
      blue: {
        bg: isSelected ? 'bg-blue-500/10' : unselectedBg,
        border: isSelected ? 'border-blue-500/50' : 'border-border',
        icon: isSelected ? 'bg-blue-500 text-white' : 'bg-muted text-muted-foreground',
        text: isSelected ? 'text-blue-600' : 'text-foreground',
      },
      primary: {
        bg: isSelected ? 'bg-primary/10' : unselectedBg,
        border: isSelected ? 'border-primary/50' : 'border-border',
        icon: isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
        text: isSelected ? 'text-primary' : 'text-foreground',
      },
    };
    return colors[color] || colors.primary;
  };

  return (
    <div className="space-y-6">
      {/* Alert Type Selector - Full width */}
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-4 gap-4">
          {alertTypes.map((type) => {
            const Icon = type.icon;
            const isSelected = selectedType === type.type;
            const colors = getColorClasses(type.color, isSelected);
            return (
              <button
                key={type.type}
                onClick={() => setSelectedType(type.type)}
                disabled={isLoading}
                className={`
                  p-4 rounded-xl transition-all duration-300 text-center border flex flex-col items-center
                  ${colors.bg} ${colors.border}
                  portal-card-shadow
                  ${isSelected ? 'border-primary/30' : 'portal-card-hover'}
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                <div className={`
                  p-2 rounded-lg mb-3 transition-all duration-300
                  ${colors.icon}
                `}>
                  <Icon className="h-4 w-4" />
                </div>
                <h3 className={`font-semibold text-sm transition-colors duration-300 ${colors.text}`}>
                  {type.label}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Pre-configuration Selectors */}
      <CreateAlertSelectors
        tenantName={tenantName}
        cloudId={cloudId}
        selectedCloud={selectedCloud}
        selectedTimeWindows={preSelectedTimeWindows}
        selectedUsers={preSelectedUsers}
        selectedServiceAccounts={preSelectedServiceAccounts}
        selectedResources={preSelectedResources}
        onTimeWindowsChange={setPreSelectedTimeWindows}
        onUsersChange={setPreSelectedUsers}
        onServiceAccountsChange={setPreSelectedServiceAccounts}
        onResourcesChange={setPreSelectedResources}
        disabled={isLoading || !tenantName || !cloudId}
      />

      {/* Chat Input - Full width */}
      <div className="relative group">
        <div className="bg-card rounded-2xl p-3 border portal-card-elevated">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <Sparkles className="h-4 w-4 text-primary/50" />
              </div>
              <input
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Describe the alert you want to create..."
                className="w-full bg-transparent text-foreground placeholder:text-muted-foreground
                  focus:outline-none pl-9 pr-4 py-2 h-10 disabled:pointer-events-none"
                disabled={isPromptInputDisabled}
              />
            </div>
            <Button
              onClick={handleSubmit}
              disabled={isSendPromptDisabled}
              size="icon"
              className="h-10 w-10 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground shrink-0 shadow-md hover:shadow-lg transition-all duration-200"
            >
              {(isLoading || isCreateAlertTaskInProgress) ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
        {sendPromptBlockedReason && (
          <div className="pointer-events-none absolute left-1/2 top-0 z-50 hidden -translate-x-1/2 -translate-y-[calc(100%+8px)] whitespace-nowrap rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md group-hover:block group-focus-within:block">
            {sendPromptBlockedReason}
          </div>
        )}
      </div>

      {/* Suggestions */}
      {showSuggestions && displayedSuggestions.length > 0 && !isLoading && (
        <div className="space-y-3">
          <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Suggestions for {selectedCloud} {selectedType} alerts
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {displayedSuggestions.map((suggestion) => {
              const Icon = suggestion.icon;
              return (
                <button
                  key={`${selectedCloud}-${selectedType}-${suggestion.title}`}
                  onClick={() => handleSuggestionClick(suggestion.prompt)}
                  className="group relative text-left p-4 rounded-2xl bg-card backdrop-blur-xl border border-border
                    portal-card-shadow portal-card-hover overflow-hidden flex items-start gap-4"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="h-5 w-5" />
                  </div>
                  
                  <div className="flex-1 min-w-0 z-10">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors duration-200">{suggestion.title}</h4>
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 -translate-x-2 translate-y-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-300 shrink-0" />
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{suggestion.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* Creative AI Loading Animation */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-12 w-full max-w-md">
            {/* Animated AI Brain */}
            <div className="relative w-36 h-36">
              {/* Outer rotating ring */}
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-primary/30 animate-[spin_8s_linear_infinite]" />
              
              {/* Middle pulsing ring */}
              <div className="absolute inset-3 rounded-full border-2 border-primary/40 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]" />
              
              {/* Inner glowing circle */}
              <div className="absolute inset-6 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 animate-pulse" />
              
              {/* Scanning line */}
              <div className="absolute inset-6 rounded-full overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/30 to-transparent animate-[scan_1.5s_ease-in-out_infinite]" 
                  style={{ height: '200%', transform: 'translateY(-50%)' }} />
              </div>
              
              {/* Center icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <Brain className="h-10 w-10 text-primary animate-pulse" />
                  <Sparkles className="absolute -top-1 -right-1 h-5 w-5 text-primary animate-bounce" />
                </div>
              </div>
              
              {/* Orbiting dots */}
              <div className="absolute inset-0 animate-[spin_3s_linear_infinite]">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-primary shadow-lg shadow-primary/50" />
              </div>
              <div className="absolute inset-0 animate-[spin_4s_linear_infinite_reverse]">
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-primary/70" />
              </div>
            </div>
            
            {/* Progress bar with percentage */}
            <div className="w-full space-y-3">
              <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                    loadingProgress === 100 
                      ? 'bg-green-500' 
                      : 'bg-gradient-to-r from-primary via-primary/80 to-primary'
                  }`}
                  style={{ width: `${loadingProgress}%` }} 
                />
              </div>
              <div className="flex justify-between items-center text-xs text-muted-foreground">
                <span>{loadingProgress}%</span>
                <span>{loadingProgress === 100 ? 'Complete' : 'Processing...'}</span>
              </div>
            </div>
            
            {/* Dynamic status text */}
            <div className="text-center space-y-2 w-full min-h-[60px]">
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-foreground">
                <span key={loadingTextIndex} className="animate-fade-in">
                  {loadingProgress === 100 ? 'Generated!' : LOADING_TEXTS[loadingTextIndex] || LOADING_TEXTS[0]}
                </span>
                {loadingProgress < 100 && (
                  <span className="inline-flex">
                    <span className="animate-[bounce_1s_infinite_0ms]">.</span>
                    <span className="animate-[bounce_1s_infinite_200ms]">.</span>
                    <span className="animate-[bounce_1s_infinite_400ms]">.</span>
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {loadingProgress === 100 
                  ? 'Your custom alerts are ready!' 
                  : 'Generating custom detection rules with AI'}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Generated Alerts Section - Using same AlertCard as management page */}
      {generatedAlerts.length > 0 && !isLoading && (
        <div className="space-y-4 animate-fade-in">
          {/* Header with actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-semibold text-foreground">Generated Alerts</h3>
              <Badge variant="secondary" className="text-xs">
                {selectedAlertIds.size} / {generatedAlerts.length} selected
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={selectAllAlerts}
                className="text-xs h-8"
              >
                <Check className="h-3 w-3 mr-1" />
                Select All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={deselectAllAlerts}
                className="text-xs h-8"
              >
                <X className="h-3 w-3 mr-1" />
                Deselect All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="text-xs h-8 text-destructive hover:text-destructive"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Clear All
              </Button>
            </div>
          </div>

          {/* Alert Cards Grid - Same format as management page */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {generatedAlerts.map((alert, index) => {
              const isSelected = selectedAlertIds.has(alert.id);
              return (
                <div
                  key={alert.id}
                  style={{ animationDelay: `${index * 50}ms` }}
                  className={`relative animate-fade-in cursor-pointer transition-all duration-200 ${
                    isSelected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background rounded-lg' : ''
                  }`}
                  onClick={() => toggleAlertSelection(alert.id)}
                >
                  {/* Selection Checkbox - Top right corner */}
                  <div className="absolute top-4 right-4 z-10">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleAlertSelection(alert.id)}
                      onClick={(e) => e.stopPropagation()}
                      className={`h-5 w-5 border-2 shadow-sm ${
                        isSelected 
                          ? 'bg-primary border-primary data-[state=checked]:bg-primary' 
                          : 'bg-background border-border'
                      }`}
                    />
                  </div>
                  
                  {/* AlertCard - Same component as management page but without switch */}
                  <AlertCard
                    alert={alert}
                    onViewDetails={(a) => {
                      setPreviewAlert(a);
                    }}
                    onToggle={() => {}}
                    hideSwitch
                  />
                </div>
              );
            })}
          </div>

          {/* Add Selected Button */}
          <div className="flex justify-center pt-4">
            <Button
              onClick={addSelectedAlerts}
              disabled={selectedAlertIds.size === 0 || isSaving}
              className="gap-2 px-8"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Add {selectedAlertIds.size} Selected Alert{selectedAlertIds.size !== 1 ? 's' : ''} to Library
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Alert Details Modal - Create mode with editing */}
      <AlertDetailsEdit
        alert={previewAlert}
        open={previewAlert !== null}
        onClose={() => setPreviewAlert(null)}
        tenantName={tenantName}
        cloudId={cloudId}
        selectedCloud={selectedCloud}
        isCreateMode
        onAlertUpdate={(updatedAlert) => {
          setGeneratedAlerts(prev => 
            prev.map(a => a.id === updatedAlert.id ? updatedAlert : a)
          );
          setPreviewAlert(null);
        }}
      />
    </div>
  );
}
